'use client';

import { useCallback, useRef } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import posthog from 'posthog-js';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useAppEvents } from '@kit/shared/events';
import { useAuthChangeListener } from '@kit/supabase/hooks/use-auth-change-listener';

import { SessionTracker } from '~/components/session-tracker';

export function AuthProvider(props: React.PropsWithChildren) {
  const dispatchEvent = useDispatchAppEventFromAuthEvent();

  const onEvent = useCallback(
    (event: AuthChangeEvent, session: Session | null) => {
      dispatchEvent(event, session?.user.id, {
        email: session?.user.email ?? '',
      });
    },
    [dispatchEvent],
  );

  useAuthChangeListener({
    onEvent,
  });

  return (
    <>
      {/* First-party session time for founder dashboard (+ PostHog start/end). */}
      <SessionTracker />
      {props.children}
    </>
  );
}

function isPostHogConfigured() {
  return Boolean(
    process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN &&
    process.env.NEXT_PUBLIC_POSTHOG_HOST,
  );
}

function identifyPostHogUser(userId: string, traits: Record<string, string>) {
  if (isPostHogConfigured()) {
    posthog.identify(userId, traits);
  }
}

function useDispatchAppEventFromAuthEvent() {
  const { emit } = useAppEvents();
  const monitoring = useMonitoring();
  const identifiedUserId = useRef<string | undefined>(undefined);

  return useCallback(
    (
      type: AuthChangeEvent,
      userId: string | undefined,
      traits: Record<string, string> = {},
    ) => {
      switch (type) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });

            if (
              identifiedUserId.current &&
              identifiedUserId.current !== userId &&
              isPostHogConfigured()
            ) {
              posthog.reset();
            }

            identifyPostHogUser(userId, traits);
            identifiedUserId.current = userId;
          }

          break;

        case 'SIGNED_OUT':
          if (isPostHogConfigured()) {
            posthog.reset();
          }

          identifiedUserId.current = undefined;
          break;

        case 'USER_UPDATED':
          if (!userId) {
            break;
          }

          identifyPostHogUser(userId, traits);
          identifiedUserId.current = userId;
          emit({
            type: 'user.updated',
            payload: { userId, ...traits },
          });

          break;
      }
    },
    [emit, monitoring],
  );
}
